import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { usePermissions } from '../contexts/PermissionsContext';
import {
  UsersRound,
  Plus,
  Edit,
  Trash2,
  Users,
  User,
  RefreshCw,
  AlertCircle,
  X,
  Download,
  Upload,
  FileSpreadsheet,
  FileText,
  Shield
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Teams = () => {
  const { permissions } = usePermissions();
  const canEdit = permissions?.teams_edit || false;
  const canDelete = permissions?.teams_delete || false;
  
  const [teams, setTeams] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingTeam, setEditingTeam] = useState(null);
  const [formData, setFormData] = useState({
    team_number: '',
    member1_id: '',
    member2_id: '',
    member_ids: [], // New: Array of selected member IDs
    order_type: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importFile, setImportFile] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('authToken');
      console.log('Fetching teams and employees with token:', token ? 'Present' : 'Missing');
      
      const [teamsResponse, employeesResponse] = await Promise.all([
        axios.get(`${API}/teams`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/employees`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      console.log('Teams response:', teamsResponse.data);
      console.log('Employees response:', employeesResponse.data);
      
      setTeams(teamsResponse.data);
      setEmployees(employeesResponse.data);
      
      console.log('Teams state set:', teamsResponse.data.length, 'teams');
      console.log('Employees state set:', employeesResponse.data.length, 'employees');
    } catch (error) {
      console.error('Error fetching data:', error);
      console.error('Error response:', error.response);
      toast.error('Fehler beim Laden der Daten: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const token = localStorage.getItem('authToken');
      const teamData = {
        ...formData,
        team_number: parseInt(formData.team_number),
        member1_id: formData.member1_id === 'none' ? '' : formData.member1_id,
        member2_id: formData.member2_id === 'none' ? '' : formData.member2_id,
        member_ids: formData.member_ids || []
      };

      console.log('Submitting team data:', teamData);

      if (editingTeam) {
        await axios.put(`${API}/teams/${editingTeam.id}`, teamData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Team erfolgreich aktualisiert');
        setShowEditDialog(false);
      } else {
        await axios.post(`${API}/teams`, teamData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Team erfolgreich erstellt');
        setShowAddDialog(false);
      }
      
      fetchData();
      resetForm();
    } catch (error) {
      console.error('Error saving team:', error);
      
      let errorMessage = 'Fehler beim Speichern des Teams';
      
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        
        // Check if detail is an array of validation errors
        if (Array.isArray(detail)) {
          errorMessage = detail.map(err => {
            if (typeof err === 'object' && err.msg) {
              return `${err.loc ? err.loc.join('.') + ': ' : ''}${err.msg}`;
            }
            return String(err);
          }).join(', ');
        } else if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (typeof detail === 'object') {
          errorMessage = JSON.stringify(detail);
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (team) => {
    if (!canEdit) {
      toast.error('Diese Funktion ist nur für Administratoren verfügbar');
      return;
    }
    
    setEditingTeam(team);
    // Combine backward compatibility members with new member_ids
    const allMembers = [];
    if (team.member1_id) allMembers.push(team.member1_id);
    if (team.member2_id) allMembers.push(team.member2_id);
    if (team.member_ids) allMembers.push(...team.member_ids);
    
    // Remove duplicates
    const uniqueMembers = [...new Set(allMembers)];
    
    setFormData({
      team_number: team.team_number.toString(),
      member1_id: team.member1_id || '',
      member2_id: team.member2_id || '',
      member_ids: uniqueMembers,
      order_type: team.order_type
    });
    setShowEditDialog(true);
  };

  const handleDelete = async (teamId, teamNumber) => {
    if (!canDelete) {
      toast.error('Diese Funktion ist nur für Administratoren verfügbar');
      return;
    }
    
    if (window.confirm(`Sind Sie sicher, dass Sie Team ${teamNumber} löschen möchten?`)) {
      try {
        const token = localStorage.getItem('authToken');
        await axios.delete(`${API}/teams/${teamId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Team erfolgreich gelöscht');
        fetchData();
      } catch (error) {
        console.error('Error deleting team:', error);
        toast.error('Fehler beim Löschen des Teams');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      team_number: '',
      member1_id: '',
      member2_id: '',
      member_ids: [],
      order_type: ''
    });
    setEditingTeam(null);
  };

  const exportExcel = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${API}/teams/export/excel`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `teams_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Excel-Export erfolgreich');
    } catch (error) {
      console.error('Error exporting Excel:', error);
      toast.error('Fehler beim Excel-Export');
    }
  };

  const exportPDF = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${API}/teams/export/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `teams_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF-Export erfolgreich');
    } catch (error) {
      console.error('Error exporting PDF:', error);
      toast.error('Fehler beim PDF-Export');
    }
  };

  const handleImport = async () => {
    if (!importFile) {
      toast.error('Bitte wählen Sie eine Datei aus');
      return;
    }

    const formData = new FormData();
    formData.append('file', importFile);

    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.post(`${API}/teams/import/excel`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success(`Import erfolgreich: ${response.data.imported} neu, ${response.data.updated} aktualisiert`);
      
      if (response.data.errors && response.data.errors.length > 0) {
        console.error('Import errors:', response.data.errors);
        toast.warning(`${response.data.errors.length} Fehler beim Import`);
      }

      setShowImportDialog(false);
      setImportFile(null);
      fetchTeams();
    } catch (error) {
      console.error('Error importing:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Import');
    }
  };

  const handleInputChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const getEmployeeName = (employeeId) => {
    const employee = employees.find(emp => emp.id === employeeId);
    return employee ? `${employee.first_name} ${employee.last_name}` : 'Nicht zugewiesen';
  };

  const getOrderTypeBadge = (orderType) => {
    const variants = {
      connect: 'bg-blue-100 text-blue-800 border-blue-300',
      gwv: 'bg-green-100 text-green-800 border-green-300',
      beides: 'bg-purple-100 text-purple-800 border-purple-300'
    };
    return variants[orderType] || 'bg-gray-100 text-gray-800 border-gray-300';
  };

  const getAvailableEmployees = () => {
    // Now employees can be in multiple teams, so return all active employees
    return employees.filter(emp => emp.status === 'aktiv');
  };

  const getEmployeeTeams = (employeeId) => {
    return teams.filter(team => 
      team.member1_id === employeeId || 
      team.member2_id === employeeId ||
      (team.member_ids && team.member_ids.includes(employeeId))
    );
  };

  const addMemberToTeam = (employeeId) => {
    if (!formData.member_ids.includes(employeeId)) {
      setFormData(prev => ({
        ...prev,
        member_ids: [...prev.member_ids, employeeId]
      }));
    }
  };

  const removeMemberFromTeam = (employeeId) => {
    setFormData(prev => ({
      ...prev,
      member_ids: prev.member_ids.filter(id => id !== employeeId)
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Lade Teams...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-slate-50 p-6" data-testid="teams-page">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
              <UsersRound className="w-8 h-8 text-emerald-600" />
              Teams 👥
            </h1>
            <p className="text-slate-600 mt-1">Verwalten Sie Ihre Arbeitsteams - Mitarbeiter können in mehreren Teams gleichzeitig sein</p>
          </div>
          
          <div className="flex gap-2">
            <Button 
              onClick={exportExcel}
              variant="outline"
              className="gap-2"
            >
              <FileSpreadsheet className="w-4 h-4" />
              Excel Export
            </Button>
            <Button 
              onClick={exportPDF}
              variant="outline"
              className="gap-2"
            >
              <FileText className="w-4 h-4" />
              PDF Export
            </Button>
            
            <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
              <DialogTrigger asChild>
                <Button variant="outline" className="gap-2">
                  <Upload className="w-4 h-4" />
                  Import
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Teams importieren</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Excel-Datei auswählen
                    </label>
                    <Input
                      type="file"
                      accept=".xlsx,.xls"
                      onChange={(e) => setImportFile(e.target.files[0])}
                    />
                    <p className="text-xs text-slate-500 mt-2">
                      Spalten: Team-Nummer, Auftragsart (Connect/GWV/Beides), Mitarbeiter (kommagetrennt)
                    </p>
                    <p className="text-xs text-slate-500 mt-1">
                      Mitarbeiter können als Personalnummer, ID oder "Vorname Nachname" angegeben werden
                    </p>
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => setShowImportDialog(false)}>
                      Abbrechen
                    </Button>
                    <Button onClick={handleImport} disabled={!importFile}>
                      <Upload className="w-4 h-4 mr-2" />
                      Importieren
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
            <DialogTrigger asChild>
              <Button 
                className="gap-2 bg-emerald-600 hover:bg-emerald-700"
                onClick={() => resetForm()}
                data-testid="add-team-button"
              >
                <Plus className="w-4 h-4" />
                Team erstellen
              </Button>
            </DialogTrigger>
            <TeamForm
              formData={formData}
              onInputChange={handleInputChange}
              onSubmit={handleSubmit}
              submitting={submitting}
              availableEmployees={getAvailableEmployees()}
              isEdit={false}
              addMemberToTeam={addMemberToTeam}
              removeMemberFromTeam={removeMemberFromTeam}
              getEmployeeTeams={getEmployeeTeams}
            />
          </Dialog>
        </div>

        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            onClick={fetchData}
            className="gap-2"
            data-testid="refresh-teams-button"
          >
            <RefreshCw className="w-4 h-4" />
            Aktualisieren
          </Button>
        </div>
      </div>

      {/* Team Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <Card className="modern-card border-l-4 border-l-blue-500">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600 mb-1">Gesamt Teams</p>
                <p className="text-3xl font-bold text-slate-900" data-testid="total-teams-stat">
                  {teams.length}
                </p>
              </div>
              <UsersRound className="w-8 h-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>

        <Card className="modern-card border-l-4 border-l-green-500">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600 mb-1">Teams mit Mitgliedern</p>
                <p className="text-3xl font-bold text-slate-900" data-testid="teams-with-members-stat">
                  {teams.filter(team => 
                    team.member1_id || team.member2_id || (team.member_ids && team.member_ids.length > 0)
                  ).length}
                </p>
              </div>
              <Users className="w-8 h-8 text-green-600" />
            </div>
          </CardContent>
        </Card>

        <Card className="modern-card border-l-4 border-l-amber-500">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600 mb-1">Aktive Mitarbeiter</p>
                <p className="text-3xl font-bold text-slate-900" data-testid="active-employees-stat">
                  {getAvailableEmployees().length}
                </p>
              </div>
              <User className="w-8 h-8 text-amber-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Teams Grid */}
      {teams.length === 0 ? (
        <Card className="modern-card text-center py-12">
          <CardContent>
            <UsersRound className="w-12 h-12 text-slate-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Keine Teams gefunden</h3>
            <p className="text-slate-600 mb-4">Erstellen Sie Ihr erstes Team.</p>
            <Button 
              className="gap-2"
              onClick={() => setShowAddDialog(true)}
              data-testid="empty-state-add-team-button"
            >
              <Plus className="w-4 h-4" />
              Erstes Team erstellen
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {teams.map((team) => (
            <Card key={team.id} className="modern-card hover:shadow-lg transition-all duration-200">
              <CardHeader className="pb-4">
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-xl flex items-center gap-2">
                      Team {team.team_number}
                      {(() => {
                        const totalMembers = (team.member_ids?.length || 0) + 
                                           (team.member1_id ? 1 : 0) + 
                                           (team.member2_id ? 1 : 0);
                        const uniqueMembers = new Set([
                          ...(team.member_ids || []),
                          ...(team.member1_id ? [team.member1_id] : []),
                          ...(team.member2_id ? [team.member2_id] : [])
                        ]);
                        
                        if (uniqueMembers.size === 0) {
                          return <AlertCircle className="w-5 h-5 text-red-500" title="Keine Mitglieder zugewiesen" />;
                        }
                        return <Badge variant="outline" className="text-xs">{uniqueMembers.size} Mitglieder</Badge>;
                      })()}
                    </CardTitle>
                    <Badge className={`${getOrderTypeBadge(team.order_type)} border mt-2`}>
                      {team.order_type.toUpperCase()}
                    </Badge>
                  </div>
                  
                  <div className="flex gap-1">
                    {canEdit && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(team)}
                        className="p-2"
                        data-testid={`edit-team-${team.id}`}
                        title="Bearbeiten (nur Admin)"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                    )}
                    {canDelete && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(team.id, team.team_number)}
                        className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                        data-testid={`delete-team-${team.id}`}
                        title="Löschen (nur Admin)"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                    {!canEdit && !canDelete && (
                      <div className="flex items-center gap-1 px-2 py-1 bg-slate-50 rounded text-xs text-slate-500">
                        <Shield className="w-3 h-3" />
                        <span>Nur Ansicht</span>
                      </div>
                    )}
                  </div>
                </div>
              </CardHeader>
              
              <CardContent className="space-y-4">
                {/* Team Members */}
                <div className="space-y-3">
                  <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    Team-Mitglieder
                  </h4>
                  
                  {(() => {
                    // Collect all members from different sources
                    const allMembers = new Set();
                    if (team.member1_id) allMembers.add(team.member1_id);
                    if (team.member2_id) allMembers.add(team.member2_id);
                    if (team.member_ids) team.member_ids.forEach(id => allMembers.add(id));
                    
                    if (allMembers.size === 0) {
                      return (
                        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-center">
                          <AlertCircle className="w-6 h-6 text-amber-600 mx-auto mb-2" />
                          <p className="text-sm text-amber-800">Keine Mitarbeiter zugewiesen</p>
                        </div>
                      );
                    }
                    
                    return Array.from(allMembers).map((memberId, index) => (
                      <div key={memberId} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                        <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center">
                          <User className="w-4 h-4 text-emerald-600" />
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-medium text-slate-700">
                            {getEmployeeName(memberId)}
                          </p>
                          <p className="text-xs text-slate-500">
                            Mitglied #{index + 1} • In {getEmployeeTeams(memberId).length} Teams
                          </p>
                        </div>
                      </div>
                    ));
                  })()}
                </div>

                {/* Team Status */}
                <div className="pt-3 border-t border-slate-100">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600">Team Status:</span>
                    {(() => {
                      const totalMembers = new Set([
                        ...(team.member_ids || []),
                        ...(team.member1_id ? [team.member1_id] : []),
                        ...(team.member2_id ? [team.member2_id] : [])
                      ]);
                      
                      if (totalMembers.size === 0) {
                        return (
                          <Badge className="bg-red-100 text-red-800 border-red-300 border">
                            Leer
                          </Badge>
                        );
                      } else if (totalMembers.size === 1) {
                        return (
                          <Badge className="bg-amber-100 text-amber-800 border-amber-300 border">
                            1 Mitglied
                          </Badge>
                        );
                      } else {
                        return (
                          <Badge className="bg-green-100 text-green-800 border-green-300 border">
                            {totalMembers.size} Mitglieder
                          </Badge>
                        );
                      }
                    })()}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <TeamForm
          formData={formData}
          onInputChange={handleInputChange}
          onSubmit={handleSubmit}
          submitting={submitting}
          availableEmployees={getAvailableEmployees()}
          isEdit={true}
          addMemberToTeam={addMemberToTeam}
          removeMemberFromTeam={removeMemberFromTeam}
          getEmployeeTeams={getEmployeeTeams}
        />
      </Dialog>
    </div>
  );
};

// Team Form Component
const TeamForm = ({ 
  formData, 
  onInputChange, 
  onSubmit, 
  submitting, 
  availableEmployees, 
  isEdit,
  addMemberToTeam,
  removeMemberFromTeam,
  getEmployeeTeams
}) => {
  return (
    <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle>
          {isEdit ? 'Team bearbeiten' : 'Neues Team erstellen'}
        </DialogTitle>
      </DialogHeader>
      
      <form onSubmit={onSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium mb-1">Team Nummer (1-30)</label>
          <Input
            type="number"
            min="1"
            max="30"
            value={formData.team_number}
            onChange={(e) => onInputChange('team_number', e.target.value)}
            placeholder="1"
            required
            data-testid="team-number-input"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Auftragsart</label>
          <Select value={formData.order_type} onValueChange={(value) => onInputChange('order_type', value)}>
            <SelectTrigger data-testid="order-type-select">
              <SelectValue placeholder="Wählen Sie..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="connect">Connect</SelectItem>
              <SelectItem value="gwv">GWV</SelectItem>
              <SelectItem value="beides">Beides</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Multi-Member Selection */}
        <div>
          <label className="block text-sm font-medium mb-2">Team-Mitglieder auswählen</label>
          <p className="text-xs text-slate-500 mb-3">
            Mitarbeiter können in mehreren Teams gleichzeitig sein. Klicken Sie auf Mitarbeiter um sie hinzuzufügen/zu entfernen.
          </p>
          
          {/* Selected Members */}
          {formData.member_ids.length > 0 && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
              <h5 className="text-sm font-medium text-green-800 mb-2">Ausgewählte Mitglieder ({formData.member_ids.length}):</h5>
              <div className="space-y-2">
                {formData.member_ids.map(memberId => {
                  const employee = availableEmployees.find(emp => emp.id === memberId);
                  if (!employee) return null;
                  
                  return (
                    <div key={memberId} className="flex items-center justify-between bg-white p-2 rounded border">
                      <span className="text-sm">
                        {employee.first_name} {employee.last_name} (#{employee.employee_number})
                      </span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeMemberFromTeam(memberId)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          
          {/* Available Employees */}
          <div className="border border-slate-200 rounded-lg max-h-48 overflow-y-auto">
            <div className="p-2 bg-slate-50 border-b border-slate-200">
              <p className="text-sm font-medium text-slate-700">Verfügbare Mitarbeiter:</p>
            </div>
            <div className="p-2 space-y-1">
              {availableEmployees.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-4">
                  Keine aktiven Mitarbeiter verfügbar
                </p>
              ) : (
                availableEmployees.map(employee => {
                  const isSelected = formData.member_ids.includes(employee.id);
                  const employeeTeams = getEmployeeTeams(employee.id);
                  
                  return (
                    <div
                      key={employee.id}
                      className={`flex items-center justify-between p-2 rounded cursor-pointer transition-colors ${
                        isSelected 
                          ? 'bg-emerald-100 border border-emerald-300' 
                          : 'hover:bg-slate-100'
                      }`}
                      onClick={() => {
                        if (isSelected) {
                          removeMemberFromTeam(employee.id);
                        } else {
                          addMemberToTeam(employee.id);
                        }
                      }}
                    >
                      <div className="flex-1">
                        <p className="text-sm font-medium">
                          {employee.first_name} {employee.last_name}
                        </p>
                        <p className="text-xs text-slate-500">
                          #{employee.employee_number} • {employeeTeams.length > 0 ? `In ${employeeTeams.length} Teams` : 'Noch in keinem Team'}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        {employeeTeams.length > 0 && (
                          <Badge variant="outline" className="text-xs">
                            {employeeTeams.map(t => t.team_number).join(', ')}
                          </Badge>
                        )}
                        {isSelected ? (
                          <Badge className="bg-emerald-500 text-white">✓</Badge>
                        ) : (
                          <Badge variant="outline">+</Badge>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>

        {/* Warning removed - employees can now be in multiple teams */}

        <div className="flex justify-end gap-2 pt-4">
          <Button type="button" variant="outline" disabled={submitting}>
            Abbrechen
          </Button>
          <Button type="submit" disabled={submitting} data-testid="save-team-button">
            {submitting ? (
              <>
                <div className="loading-spinner mr-2"></div>
                Speichere...
              </>
            ) : (
              isEdit ? 'Aktualisieren' : 'Erstellen'
            )}
          </Button>
        </div>
      </form>
    </DialogContent>
  );
};

export default Teams;